import { useEffect, useState } from 'react';
import { DEFAULT_ICON, ICONS, ICON_GROUPS, resolveIcon, type IconEntry } from '../lib/icons';

type Props = {
  value: string | null;
  tags: string[];
  name: string;
  onChange: (emoji: string | null) => void;
};

export default function IconPicker({ value, tags, name, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const suggested = resolveIcon({ tags, name });
  const current: IconEntry = value
    ? ICONS.find((i) => i.emoji === value) ?? suggested ?? DEFAULT_ICON
    : suggested;

  const isAuto = !value;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-3 group"
      >
        <span
          className="h-16 w-16 rounded-2xl flex items-center justify-center text-4xl shadow-card"
          style={{
            background: `linear-gradient(135deg, ${current.gradient[0]}, ${current.gradient[1]})`,
          }}
          aria-hidden
        >
          {current.emoji}
        </span>
        <span className="text-left">
          <span className="block text-sm font-semibold text-ink group-hover:text-brand transition">
            {isAuto ? 'Icona suggerita' : 'Icona scelta'}
          </span>
          <span className="block text-xs text-muted mt-0.5">
            {isAuto ? 'tocca per cambiare' : current.name}
          </span>
        </span>
      </button>

      {open && (
        <IconPickerSheet
          current={current.emoji}
          suggested={suggested.emoji}
          isAuto={isAuto}
          onPick={(emoji) => {
            onChange(emoji);
            setOpen(false);
          }}
          onAuto={() => {
            onChange(null);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function IconPickerSheet({
  current,
  suggested,
  isAuto,
  onPick,
  onAuto,
  onClose,
}: {
  current: string;
  suggested: string;
  isAuto: boolean;
  onPick: (emoji: string) => void;
  onAuto: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center animate-riseIn"
      role="dialog"
      aria-modal="true"
      aria-label="scegli icona"
    >
      <button
        type="button"
        className="absolute inset-0 bg-ink/35 backdrop-blur-sm"
        onClick={onClose}
        aria-label="chiudi"
      />
      <div className="relative bg-card w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-cardHover max-h-[85vh] flex flex-col">
        <header className="flex items-center justify-between px-5 pt-5 pb-3 sticky top-0 bg-card rounded-t-3xl">
          <div>
            <h3 className="text-lg font-bold">Scegli un'icona</h3>
            <p className="text-xs text-muted mt-0.5">apparirà come copertina se non carichi una foto</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full bg-surface flex items-center justify-center text-lg no-tap"
            aria-label="chiudi"
          >
            ✕
          </button>
        </header>

        {!isAuto && (
          <div className="px-5 pb-3">
            <button
              type="button"
              onClick={onAuto}
              className="w-full text-left rounded-2xl bg-brandSoft px-4 py-3 flex items-center gap-3"
            >
              <span className="text-2xl">✨</span>
              <span className="flex-1 text-sm">
                <span className="block font-semibold text-brand">Usa suggerimento automatico</span>
                <span className="block text-xs text-brand/80 mt-0.5">
                  sceglierò {suggested} in base al nome e alle categorie
                </span>
              </span>
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-3 pb-6 pt-2">
          {ICON_GROUPS.map((g) => {
            const items = ICONS.filter((i) => i.group === g.key);
            if (items.length === 0) return null;
            return (
              <section key={g.key} className="mb-5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted px-2 mb-2">
                  {g.label}
                </h4>
                <div className="grid grid-cols-6 gap-2">
                  {items.map((i) => {
                    const active = i.emoji === current;
                    return (
                      <button
                        key={i.emoji}
                        type="button"
                        onClick={() => onPick(i.emoji)}
                        className={`aspect-square rounded-xl flex items-center justify-center text-3xl transition no-tap ${
                          active
                            ? 'ring-2 ring-brand ring-offset-2 ring-offset-card scale-105'
                            : 'hover:bg-surface active:scale-95'
                        }`}
                        style={
                          active
                            ? {
                                background: `linear-gradient(135deg, ${i.gradient[0]}, ${i.gradient[1]})`,
                              }
                            : undefined
                        }
                        aria-label={i.name}
                      >
                        {i.emoji}
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
